import { oClient } from "@cayo/shared/libs/oDataClient";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import { ajaxUtils } from "@cayo/shared/utils/ajax";
import { Credential, InitialStateType, Partition, CreationResult, GMSACredential } from "../types";

export interface DownloadResponse {
  headers: Record<string, string>;
  data: Blob;
}

export type DownloadError = Response | Error;

export async function getForest() {
  const res = await fetch(`${endpoints.publicUrl}/v0/root`);
  return await res.json();
}

export function validateCred(data: Credential): Promise<{ partitions: Partition[] }> {
  return new Promise((resolve, reject) => {
    fetch(`${endpoints.publicUrl}/v0/configuration/managedDomains/discoverForest`, {
      method: "POST",
      body: JSON.stringify(data),
    })
      .then(async (response) => {
        const ok = response.ok;
        const res = await response.json();
        return await Promise.resolve({ ok, data: res });
      })
      .then(({ ok, data }) => {
        if (ok) {
          return resolve(data);
        }

        reject(data);
      })
      .catch((e) => reject(e));
  });
}

export function validateAccountName(data: GMSACredential) {
  return new Promise((resolve, reject) => {
    fetch(`${endpoints.publicUrl}/v0/configuration/connectionCredentials/validateManagedAccount`, {
      method: "POST",
      body: JSON.stringify(data),
    })
      .then(async (response) => {
        const ok = response.ok;

        return await Promise.resolve({ ok, response });
      })
      .then(({ ok, response }) => {
        if (ok) {
          return resolve(ok);
        }

        reject(response);
      })
      .catch((e) => reject(e));
  });
}

export async function getAgents() {
  return new Promise((resolve, reject) => {
    oClient(endpoints.publicUrl)
      .get("v0/configuration/workerSystemNodes")
      .query({
        $filter: "disabled eq false",
        $select:
          "id,objectPath,objectType,builtIn,snapshotDateTime,categories,disabled,errors,compoundStatus,name,version,heartBeatLastSuccessDateTime",
      })
      .then((response) => {
        resolve(response);
      })
      .catch(() => reject());
  });
}

function createODataFilter(targetNames: string[]): string {
  if (targetNames.length === 0) {
    return "";
  }

  const filterString = targetNames.map((name) => `targetName eq '${name}'`).join(" or ");
  return `${filterString}`;
}

export async function getJobs(managedPartitions: string[]) {
  const filter = createODataFilter(managedPartitions);
  return new Promise((resolve, reject) => {
    oClient(endpoints.publicUrl)
      .get("v0/changeAudit/jobs")
      .query({ $filter: filter, $select: "name,disabled" })
      .then((response) => {
        resolve(response);
      })
      .catch(() => reject());
  });
}

export async function createDomainsAndPartitions(state: InitialStateType): Promise<CreationResult> {
  return new Promise(async (resolve) => {
    const partitions = state.selectedPartitions;
    const form = {
      user: state.credentials.user,
      password: state.credentials.password,
      ...state.creationForm,
    };

    if (state.creationForm.credentialUsage === "userAccount") {
      delete (form as Partial<typeof form>).managedAccountName;
    }

    const result: CreationResult = {
      failed: [],
      managed: [],
    };

    for (let index = 0; index < partitions.length; index++) {
      const partition = partitions[index] as Partition;
      const response = await fetch(
        `${endpoints.publicUrl}/v0/configuration/managedDomains/addPartition`,
        {
          method: "POST",
          body: JSON.stringify({
            ...form,
            name: partition.contextName,
          }),
        }
      );
      const ok = response.ok;
      const res = await response.json();

      if (ok) {
        result.managed.push(partition.displayName);
      } else {
        result.failed.push({
          name: partition.displayName,
          message: res.message,
        });
      }
    }
    resolve(result);
  });
}

export function createDownloadADConnector(): Promise<DownloadResponse> {
  return new Promise<DownloadResponse>((resolve, reject) => {
    fetch(`${endpoints.publicUrl}/v0/configuration/workerSystemNodes/cayo.graph.registerWorker`, {
      method: "POST",
      body: JSON.stringify({
        hostName: "ADConnector",
      }),
    })
      .then(async (response): Promise<{ ok: boolean; response: Response }> => {
        return {
          ok: response.ok,
          response,
        };
      })
      .then(async ({ ok, response }) => {
        if (!ok) {
          reject(response);
          return;
        }

        const headers = ajaxUtils.getHeaders(response) as Record<string, string>;
        const data = await response.blob();

        resolve({
          headers,
          data,
        });
      })
      .catch((e: Error) => reject(e));
  });
}

export function waitForAgentOnline() {
  return new Promise((resolve, reject) => {
    const intervalId = setInterval(async () => {
      try {
        const agents: any = await getAgents();

        if (!agents?.length) {
          return;
        }

        const agent = agents[0];

        if (agent.compoundStatus === "online") {
          clearInterval(intervalId);
          resolve(agents);
        }
      } catch (error) {
        clearInterval(intervalId);
        reject(error);
      }
    }, 5000);
  });
}
